import { useState, useCallback, useEffect } from 'react';
import { gql, useApolloClient } from '@apollo/client';

type ContentProgress = {
    completed: boolean;
    time: number;
};

type FragmentResult = {
    contentProgress?: ContentProgress;
};

const CONTENT_FRAGMENT = gql`
    fragment ContentProgress on Content {
        contentProgress {
            completed
            time
        }
    }
`;

const VIDEO_FRAGMENT = gql`
    fragment VideoProgress on Video {
        contentProgress {
            completed
            time
        }
    }
`;

const READ_CACHE_INTERVAL = 2000;

/**
 * TODO: Refactor to use Apollo's `useFragment` hook once updated to 3.8.0
 */
export const useCacheReadProgress = (id: string) => {
    const client = useApolloClient();

    const readProgress = useCallback(() => {
        const videoData = client.cache.readFragment<FragmentResult>({
            id: `Video:${id}`,
            fragment: VIDEO_FRAGMENT,
            optimistic: true
        });
        const contentData = client.cache.readFragment<FragmentResult>({
            id: `Content:${id}`,
            fragment: CONTENT_FRAGMENT,
            optimistic: true
        });

        if (videoData?.contentProgress?.completed) {
            return videoData.contentProgress;
        }
        if (contentData?.contentProgress?.completed) {
            return contentData.contentProgress;
        }
        return videoData?.contentProgress || contentData?.contentProgress;
    }, [client, id]);

    const [progress, setProgress] = useState<ContentProgress | undefined>(readProgress());

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(readProgress());
        }, READ_CACHE_INTERVAL);

        return () => {
            clearInterval(interval);
        };
    }, [client, id, readProgress]);

    return progress;
};
